import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import {
  selectKycAccountVerified,
  selectKycAccountVerifiedMexico,
} from '@/store/selectors/kycSelectors';
import { HeaderBanner } from '@/components/shared/banners/HeaderBanner';
import LinearGradient from '@/components/shared/gradient/LinearGradient';
import InfiniteScrollStreamline from '@/components/shared/streamline/InfiniteScrollStreamline';
import React, { useMemo } from 'react';
import { isAndroid } from 'react-device-detect';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import useAuthentication from '@/hooks/useAuthentication';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { TransactionType, useFluidOpen } from '@/features/cashier/fluid/fluidPaymentsStore';
import { useCashierShow } from '@/features/cashier/useCashier';
import { bannerDesktopImage, bannerGif, bannerMobileImage } from '@/utils/image-utils';
import { useIntl } from 'react-intl';
import { useRegistration } from '@/hooks/useRegistration';
import { useLogin } from '@/hooks/useLogin';
import {
  BffCmsHeaderBanner,
  BffCmsHeaderBannerActionType,
  BffCmsHeaderBannerVisibility,
} from '@lucky7ventures/bff-types';
import { selectHasWelcomeBonuses } from '@/store/bonuses/selectors';

const isHeaderBannerVisible = (
  banner: BffCmsHeaderBanner,
  {
    isAuthenticated,
    isVerified,
    hasWelcomeBonus,
    isAndroid,
  }: {
    isAuthenticated: boolean;
    isVerified: boolean;
    hasWelcomeBonus: boolean;
    isAndroid: boolean;
  },
): boolean => {
  const rules = banner.visibility;
  if (rules.length === 0) {
    return true;
  }
  return rules.every(rule => {
    switch (rule) {
      case BffCmsHeaderBannerVisibility.AUTHENTICATED:
        return isAuthenticated;
      case BffCmsHeaderBannerVisibility.UNAUTHENTICATED:
        return !isAuthenticated;
      case BffCmsHeaderBannerVisibility.VERIFIED:
        return isAuthenticated && isVerified;
      case BffCmsHeaderBannerVisibility.UNVERIFIED:
        return isAuthenticated && !isVerified;
      case BffCmsHeaderBannerVisibility.HAS_WELCOME_BONUS:
        return isAuthenticated && hasWelcomeBonus;
      case BffCmsHeaderBannerVisibility.ANDROID_ONLY:
        return isAndroid;
      default:
        return false;
    }
  });
};
const HeaderBannerCarousel: React.FC<{ headerBanners: BffCmsHeaderBanner[] }> = ({
  headerBanners,
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const hasWelcomeBonus = useSelector(selectHasWelcomeBonuses);
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const { openLogin } = useLogin();
  const isVerifiedMexico = useSelector(selectKycAccountVerifiedMexico);
  const isVerifiedRest = useSelector(selectKycAccountVerified);
  const isVerified = isAuthenticated && (isVerifiedMexico || isVerifiedRest);
  const navigate = useAbsoluteNavigate();
  const { openAuthentication, isReturningUser } = useAuthentication();
  const openFluid = useFluidOpen();
  const openCashier = useCashierShow();
  const intl = useIntl();

  // Registration is removed in restricted mode — fall back to login so the banner CTA never
  // opens (a now no-op) registration.
  const handleAuthAction = () => {
    if (isRegistrationRemoved) {
      openLogin({ openedFrom: OpenedFrom.Games });
    } else if (isReturningUser) {
      openAuthentication(OpenedFrom.Games);
    } else {
      openRegistration({ openedFrom: OpenedFrom.Games });
    }
  };

  const fallbackHeader = (
    <HeaderBanner
      id="default-header-banner"
      buttonOnClick={() => {
        triggerGTag(GTagEvents.games_banner_cta, { slug: 'jackpots' });
        if (isAuthenticated) {
          navigate('/games/jackpots');
        } else {
          handleAuthAction();
        }
      }}
      title={intl.formatMessage({ id: 'games.appBanner.heading' })}
      desktopImage={bannerDesktopImage('app-banner/desktop.jpg')}
      mobileImage={bannerMobileImage('app-banner/mobile.jpg')}
      actionText={
        isAuthenticated
          ? intl.formatMessage({ id: 'gameCard.play' })
          : intl.formatMessage({ id: 'header.login' })
      }
    />
  );

  const handleActionClick = (
    action: BffCmsHeaderBannerActionType,
    value: string | null,
    slug: string,
  ) => {
    switch (action) {
      case BffCmsHeaderBannerActionType.AUTHORIZE:
        handleAuthAction();
        break;
      case BffCmsHeaderBannerActionType.NAVIGATE:
        navigate(value ?? '');
        break;
      case BffCmsHeaderBannerActionType.OPEN_CASHIER:
        openCashier({ transactionType: value as unknown as TransactionType });
        break;
      case BffCmsHeaderBannerActionType.OPEN_FLUID:
        openFluid(value as unknown as TransactionType);
        break;
      case BffCmsHeaderBannerActionType.OPEN_WINDOW:
        window.open(value ?? '', '_blank');
        break;
    }
    triggerGTag(GTagEvents.games_banner_cta, { slug });
  };

  const items = useMemo(() => {
    const filtered = headerBanners
      .filter(banner =>
        isHeaderBannerVisible(banner, {
          isAuthenticated,
          isVerified,
          isAndroid,
          hasWelcomeBonus,
        }),
      )
      .map((banner, index) => {
        const actionText =
          isReturningUser && banner.action.textReturningUser
            ? banner.action.textReturningUser
            : banner.action.text;
        return (
          <HeaderBanner
            id={`${banner.slug}-${banner.visibility.join('-')}`}
            key={`${banner.slug}-${banner.visibility.join('-')}-${index}`}
            title={banner.title}
            mobileSubTitle={banner.mobileSubTitle}
            mobileTitle={banner.mobileTitle}
            subtitle={banner.subTitle}
            terms={banner.terms}
            mobileImage={bannerMobileImage(banner.mobileImage)}
            desktopImage={bannerDesktopImage(banner.desktopImage)}
            gif={banner.gif ? bannerGif(banner.gif) : undefined}
            actionText={actionText}
            buttonOnClick={() => {
              handleActionClick(banner.action.type, banner.action.value, banner.slug);
            }}
          />
        );
      });
    return filtered.length > 0 ? filtered : [fallbackHeader];
  }, [headerBanners, isAuthenticated, hasWelcomeBonus, isVerified]);
  return (
    <div className="relative">
      <LinearGradient
        from="rgba(255, 255, 255, 0.8)"
        to="rgba(10, 107, 119, 1)"
        direction="top"
        transitionPoint="36%"
      />
      <InfiniteScrollStreamline autoplay children={items} />
    </div>
  );
};

export default HeaderBannerCarousel;
